import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { pluck } from 'rxjs/operators';
import { User } from 'src/app/core/objects/user';
import { UserService } from 'src/app/core/services/user.service';
import { ProjectApiService } from '../../core/services/project-api.service';
import { ProjectService } from '../../core/services/project.service';
import { LoadingSpaceCumulation, Recommendation } from '../../core/types';
import { FADE_IN_OUT_ANIMATION, HEIGHT_ANIMATION } from '../../shared/animations/animations';
import { Message, MessageService } from '../../core/services/message.service';
import { Subscription } from 'rxjs';
import { Project } from '../../core/objects/project';

@Component({
    selector: 'gl-recommendations',
    templateUrl: './recommendations.component.html',
    styleUrls: ['./recommendations.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION, HEIGHT_ANIMATION],
    standalone: false
})
export class RecommendationsComponent implements OnDestroy, OnInit {
  @HostBinding('@show') public show;
  public sortedRecommendations: Recommendation[];
  public activeRecommendations: boolean;
  public activeHeader = true;
  public user: User;
  private subscriptions: Subscription[] = [];
  public isRecommendations = false;
  public isCalculating = false;
  public isError = false;
  private isRecommendationsClosed = false;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private userService: UserService,
    private messageService: MessageService,
  ) {
    this.isRecommendationsClosed = false;
    this.userService.user.subscribe((user: User) => (this.user = user));
    this.subscriptions.push(
      this.messageService.getMessages().subscribe((message: Message): void => {
        if (message?.name === 'turnOffRecommendations') {
          this.activeHeader = false;
          this.activeRecommendations = false;
          this.sortedRecommendations = null;
          this.isRecommendations = false;
          this.isRecommendationsClosed = true;
          this.projectApiService.closeRecommendation().subscribe();
        }
        if (message?.name === 'closeWindowRecommendations') {
          this.isRecommendations = false;
          this.activeRecommendations = false;
          this.sortedRecommendations = null;
        }
        if (message?.name === 'turnOnRecommendations') {
          this.isRecommendations = true;
          this.isRecommendationsClosed = false;
          this.getCalculatedRecommendations();
        }
        if (message?.name === 'clearRecommendations') {
          this.sortedRecommendations = null;
        }
      }),
    );
  }

  private joinSameNameRecommendations(recommendations: Recommendation[]): Recommendation[] {
    return recommendations.map((recommendation: Recommendation) => {
      const recommendedLoadingSpacesJoined: LoadingSpaceCumulation[] =
        recommendation.recommendedLoadingSpaces.reduce(
          (acc: LoadingSpaceCumulation[], curr: LoadingSpaceCumulation) => {
            const existingIndex: number = acc.findIndex(
              (item: LoadingSpaceCumulation): boolean =>
                item.loadingSpace.name === curr.loadingSpace.name,
            );
            if (existingIndex !== -1) {
              acc[existingIndex].quantity += curr.quantity;
            } else {
              acc.push({ ...curr });
            }
            return acc;
          },
          [],
        );
      const joinedRecommendation: Recommendation = {
        ...recommendation,
        recommendedLoadingSpaces: recommendedLoadingSpacesJoined,
      };
      return joinedRecommendation;
    });
  }

  public choseRecommendation(recommendation: Recommendation): void {
    this.projectApiService.acceptRecommendation(recommendation.id).subscribe((): void => {
      this.projectService.resetRecommendations();
      this.messageService.sendMessage({ name: 'closeRecommendations' });
    });
  }

  public closeRecommendation(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isRecommendationsClosed = true;
    this.projectApiService.closeRecommendation().subscribe((): void => {
      this.sortedRecommendations = null;
      this.activeRecommendations = false;
      this.isRecommendations = false;
      this.projectService.resetRecommendations();
      this.messageService.sendMessage({ name: 'closeRecommendations' });
    });
  }

  private checkIfTokenExists(): boolean {
    const token = localStorage.getItem('token');
    return token !== null && token !== undefined;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.isRecommendationsClosed = true;
    const isTokenExists = this.checkIfTokenExists();
    if (this.user && isTokenExists) {
      this.projectApiService.closeRecommendation().subscribe(() => {
        this.sortedRecommendations = null;
        this.activeRecommendations = false;
        this.isRecommendations = false;
        this.messageService.sendMessage({ name: 'closeRecommendations' });
      });
    }
  }

  public getCalculatedRecommendations(): void {
    this.isCalculating = true;
    this.isError = false;
    this.activeRecommendations = false;
    this.projectApiService
      .calculateRecommendations()
      .pipe(pluck('recommendations'))
      .subscribe(
        (recommendations: Recommendation[]): void => {
          if (this.isRecommendationsClosed) {
            this.isRecommendationsClosed = false;
            return;
          }
          this.sortedRecommendations = this.joinSameNameRecommendations(recommendations);
          if (recommendations[1]) {
            const recommendationsToSort: LoadingSpaceCumulation[] =
              this.sortedRecommendations[1].recommendedLoadingSpaces;
            const holder: Record<string, number> = {};
            recommendationsToSort.forEach((recommendation: LoadingSpaceCumulation): void => {
              if (Object.prototype.hasOwnProperty.call(holder, recommendation.loadingSpace.name)) {
                holder[recommendation.loadingSpace.name] =
                  holder[recommendation.loadingSpace.name] + recommendation.quantity;
              } else {
                holder[recommendation.loadingSpace.name] = recommendation.quantity;
              }
            });
            const obj2 = [];
            for (const prop in holder) {
              obj2.push({ loadingSpace: { name: prop }, quantity: holder[prop] });
            }
            const sortedObj = obj2.sort(
              (a: SortedRecommendations, b: SortedRecommendations) => b.quantity - a.quantity,
            );
            this.sortedRecommendations[1] = {
              id: this.sortedRecommendations[1].id,
              hasDefaultSpacesOnly: this.sortedRecommendations[1].hasDefaultSpacesOnly,
              recommendedLoadingSpaces: sortedObj,
            };
          }
          this.activeRecommendations = true;
          this.isRecommendations = true;
          this.isCalculating = false;
        },
        (): void => {
          this.isCalculating = false;
          this.isError = true;
          this.activeRecommendations = false;
        },
      );
  }

  ngOnInit(): void {
    this.isRecommendationsClosed = false;
    this.init();
  }

  private init(): void {
    this.subscriptions.push(
      this.projectService.project.subscribe((project: Project): void => {
        if (project.options?.calculateRecommendations && project.recommendations?.length > 0) {
          this.activeRecommendations = true;
          this.isRecommendations = true;
          this.sortedRecommendations = this.joinSameNameRecommendations(project.recommendations);
        } else if (
          project.recommendations === undefined &&
          !project.options?.calculateRecommendations
        ) {
          this.activeRecommendations = false;
          this.isRecommendations = false;
        } else if (
          project.options?.calculateRecommendations &&
          (project.recommendations?.length === 0 || project.recommendations === undefined)
        ) {
          this.activeRecommendations = true;
          this.isRecommendations = true;
          this.sortedRecommendations = [];
        }
      }),
    );
  }
}

interface SortedRecommendations {
  loadingSpace: {
    name: string;
  };
  quantity: number;
}
